/**
 * Base API client with authentication
 * Matches Godot's auth pattern using same localStorage keys
 */

import { captureError } from '@/lib/sentry';
import { isQuotaBlockedRequest, QUOTA_GATED_PATH_FRAGMENTS } from './plan-errors';
import { formatQuotaError, showQuotaErrorToast } from './quota-error';

// Environment detection - matches Godot's EnvConfig
function getApiHost(): string {
  // Check for environment variable first (set in .env files)
  if (import.meta.env.VITE_API_HOST) {
    return import.meta.env.VITE_API_HOST;
  }

  // Default to localhost for development
  return 'http://localhost:8000';
}

const API_HOST = getApiHost();

// localStorage keys - must match Godot's auth_manager.gd
const TOKEN_KEYS = {
  accessToken: 'pd_access_token',
  refreshToken: 'pd_refresh_token',
  userId: 'pd_user_id',
} as const;

// Refresh deduplication — all concurrent 401 handlers share a single in-flight refresh
let refreshPromise: Promise<boolean> | null = null;

// Event name for cross-module token sync (React → Godot, tab → tab)
export const TOKENS_UPDATED_EVENT = 'pd-tokens-updated' as const;

// Fires when an impersonation session expires or is cleared cross-tab.
// Listeners render the expired-session modal.
export const IMPERSONATION_EXPIRED_EVENT = 'pd-impersonation-expired' as const;

/**
 * Fires when a quota-gated mutation either succeeded or hit a plan-limit
 * 4xx. A single listener (registered near the React Query provider) calls
 * `queryClient.invalidateQueries(billingKeys.status())`. Decoupled from
 * React Query so non-React callers (godot bridge, tests) can also dispatch.
 */
export const BILLING_INVALIDATE_EVENT = 'billing:invalidate' as const;

/**
 * Dispatch {@link BILLING_INVALIDATE_EVENT} so the credit pill refreshes after
 * a billing change (e.g. a failed/cancelled job's backend refund). Shared by
 * job pollers; no-op outside the browser.
 */
export function notifyBillingInvalidate(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BILLING_INVALIDATE_EVENT));
}

export interface ApiResponse<T = unknown> {
  ok: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
  errorParams?: Record<string, unknown>;
  status: number;
  // Set by apiRequest when a quota-gated URL returns a plan-limit errorCode.
  // Consumers (captureError) use this to skip Sentry reporting for expected
  // product-signal responses. See lib/api/plan-errors.ts.
  suppressSentry?: boolean;
  /**
   * True when `apiRequest` already surfaced the rich quota toast for this
   * response. Per-page catches use this (via `swallowQuotaToast` /
   * `swallowQuotaResult`) to skip their generic fallback toast and avoid
   * double-toasting.
   */
  quotaToastShown?: boolean;
}

/**
 * Structured API error thrown by hooks for React Query error boundaries
 */
export class ApiError extends Error {
  userMessage?: string;
  status: number;
  errorCode?: string;
  errorParams: Record<string, unknown>;
  suppressSentry?: boolean;
  quotaToastShown?: boolean;

  constructor(response: ApiResponse) {
    super(response.error || 'Request failed');
    this.name = 'ApiError';
    this.status = response.status;
    this.errorCode = response.errorCode;
    this.errorParams = response.errorParams || {};
    this.suppressSentry = response.suppressSentry;
    this.quotaToastShown = response.quotaToastShown;
    // Only surface backend message for 4xx (intentional user-facing errors)
    // 5xx errors get undefined — pages fall back to generic messages
    this.userMessage =
      response.status >= 400 && response.status < 500
        ? response.error
        : undefined;
  }
}

/**
 * Extract a user-facing message from an error, with fallback
 */
export function getUserMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.userMessage) return err.userMessage;
  return fallback;
}

/**
 * Get auth tokens from localStorage
 */
export function getAuthTokens() {
  return {
    accessToken: localStorage.getItem(TOKEN_KEYS.accessToken) || '',
    refreshToken: localStorage.getItem(TOKEN_KEYS.refreshToken) || '',
    userId: localStorage.getItem(TOKEN_KEYS.userId) || '',
  };
}

/**
 * Save auth tokens to localStorage
 */
export function setAuthTokens(
  accessToken: string,
  refreshToken?: string,
  userId?: string,
) {
  // Write refresh token BEFORE access token so that cross-tab `storage` event
  // handlers (which trigger on access token change) read the updated refresh token.
  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.refreshToken, refreshToken);
  }
  if (userId) {
    localStorage.setItem(TOKEN_KEYS.userId, userId);
  }
  localStorage.setItem(TOKEN_KEYS.accessToken, accessToken);
  scheduleProactiveRefresh();

  // Same-tab localStorage writes don't fire the browser's `storage` event, so
  // notify in-tab subscribers explicitly (otherwise useImpersonation et al
  // never resync until the next cross-tab update).
  notifyGodotTokensUpdated(accessToken, refreshToken);
}

/**
 * Clear auth tokens from localStorage
 */
export function clearAuthTokens() {
  cancelProactiveRefresh();
  localStorage.removeItem(TOKEN_KEYS.accessToken);
  localStorage.removeItem(TOKEN_KEYS.refreshToken);
  localStorage.removeItem(TOKEN_KEYS.userId);
}

/**
 * Extract the subject user id from a decoded JWT payload. This backend
 * mints the `id` claim; `sub` is accepted as a fallback so the check is
 * robust if the token shape ever shifts to the standard OIDC convention.
 */
export function jwtSubject(payload: Record<string, unknown> | null): string | null {
  if (!payload) return null;
  if (typeof payload.id === 'string' && payload.id.length > 0) return payload.id;
  if (typeof payload.sub === 'string' && payload.sub.length > 0) return payload.sub;
  return null;
}

/**
 * Check if user is authenticated.
 * Validates the JWT subject matches the stored user_id — a mismatch means
 * tampered or drifted state and is treated as unauthenticated.
 */
export function isAuthenticated(): boolean {
  const { accessToken, userId } = getAuthTokens();
  if (!accessToken || !userId) return false;
  return jwtSubject(decodeJwtPayload(accessToken)) === userId;
}

/**
 * True if the current access token carries an `impersonated_by` claim.
 */
export function isImpersonating(): boolean {
  const { accessToken } = getAuthTokens();
  if (!accessToken) return false;
  const payload = decodeJwtPayload(accessToken);
  return typeof payload?.impersonated_by === 'string' && payload.impersonated_by.length > 0;
}


/**
 * Dispatch a custom event so in-tab listeners (Godot bridge, impersonation
 * banner, analytics) pick up updated tokens. If `refreshToken` is omitted,
 * falls back to the currently stored value so callers can broadcast an access
 * token change without re-stating the refresh token.
 */
export function notifyGodotTokensUpdated(
  accessToken: string,
  refreshToken?: string,
): void {
  if (typeof window === 'undefined') return;
  const effectiveRefresh =
    refreshToken || localStorage.getItem(TOKEN_KEYS.refreshToken) || '';
  window.dispatchEvent(
    new CustomEvent(TOKENS_UPDATED_EVENT, {
      detail: { accessToken, refreshToken: effectiveRefresh },
    }),
  );
}

// --- Proactive token refresh ---

/** Decode the payload (middle segment) of a JWT. Returns null on any failure. */
export function decodeJwtPayload(
  token: string,
): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    base64 += '='.repeat((4 - (base64.length % 4)) % 4);
    const json = JSON.parse(atob(base64));
    return typeof json === 'object' && json !== null ? json : null;
  } catch {
    return null;
  }
}

/** Extract the `exp` claim (seconds since epoch) from a JWT access token. */
function decodeJwtExp(token: string): number | null {
  const payload = decodeJwtPayload(token);
  return typeof payload?.exp === 'number' ? payload.exp : null;
}

let proactiveRefreshTimer: ReturnType<typeof setTimeout> | null = null;
const REFRESH_BUFFER_SECONDS = 60;

/** Schedule a token refresh shortly before the access token expires. */
function scheduleProactiveRefresh(): void {
  cancelProactiveRefresh();

  const { accessToken } = getAuthTokens();
  if (!accessToken) return;

  // Impersonation tokens have no refresh token; a scheduled refresh
  // would fail and cascade into redirectToLogin, defeating the expired-modal UX.
  const payload = decodeJwtPayload(accessToken);
  if (payload?.impersonated_by) return;

  const exp = decodeJwtExp(accessToken);
  if (exp === null) return;

  const msUntilRefresh = (exp - REFRESH_BUFFER_SECONDS) * 1000 - Date.now();

  if (msUntilRefresh <= 0) {
    refreshAccessToken();
    return;
  }

  proactiveRefreshTimer = setTimeout(() => {
    proactiveRefreshTimer = null;
    refreshAccessToken();
  }, msUntilRefresh);
}

/** Cancel any pending proactive refresh (called on logout / token clear). */
export function cancelProactiveRefresh(): void {
  if (proactiveRefreshTimer !== null) {
    clearTimeout(proactiveRefreshTimer);
    proactiveRefreshTimer = null;
  }
}

/**
 * Refresh the access token using refresh token.
 * Deduplicated: concurrent callers share a single in-flight refresh.
 */
async function refreshAccessToken(): Promise<boolean> {
  // If a refresh is already in flight, piggyback on it
  if (refreshPromise) {
    return refreshPromise;
  }

  const { refreshToken } = getAuthTokens();

  if (!refreshToken) {
    console.warn('[API] No refresh token available');
    return false;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_HOST}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        // 400/401 = expired or revoked refresh token (routine session expiry)
        // 5xx = server-side issue worth alerting on
        if (response.status >= 500) {
          console.error('[API] Token refresh server error:', response.status);
          captureError(new Error(`Token refresh failed: ${response.status}`), {
            tags: { operation: 'token_refresh' },
            status: response.status,
          });
        } else {
          console.warn('[API] Token refresh rejected:', response.status);
        }
        return false;
      }

      const data = await response.json();

      if (data.access_token) {
        setAuthTokens(data.access_token, data.refresh_token || undefined);
        return true;
      }

      return false;
    } catch (error) {
      console.error('[API] Token refresh error:', error);
      captureError(error, { tags: { operation: 'token_refresh' } });
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Make an authenticated API request with automatic token refresh
 */
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
): Promise<ApiResponse<T>> {
  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_HOST}/${endpoint.replace(/^\//, '')}`;

  const { accessToken } = getAuthTokens();

  // Don't set Content-Type for FormData - browser will set multipart boundary
  const isFormData = options.body instanceof FormData;

  const baseHeaders: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    Accept: 'application/json',
  };

  // Normalize options.headers (could be Headers, string[][], or plain object)
  if (options.headers) {
    const h = new Headers(options.headers);
    h.forEach((value, key) => {
      baseHeaders[key] = value;
    });
  }

  const headers = baseHeaders;

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const makeRequest = async (): Promise<Response> => {
    return fetch(url, {
      ...options,
      headers,
    });
  };

  let response = await makeRequest();

  // Handle 401 - try to refresh token and retry
  if (response.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Update auth header with new token
      const { accessToken: newToken } = getAuthTokens();
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } else {
      // Refresh failed. For impersonation sessions, show the expired modal
      // instead of redirecting to login (admin should return to admin panel,
      // not Poser's login page).
      const wasImpersonating = accessToken
        ? !!decodeJwtPayload(accessToken)?.impersonated_by
        : false;
      clearAuthTokens();
      if (wasImpersonating) {
        // Banner modal + godot-bridge both listen to this event; the bridge
        // side is a best-effort latency optimization — correctness still
        // holds via Godot's own request_client 401 branch on next call.
        window.dispatchEvent(new CustomEvent(IMPERSONATION_EXPIRED_EVENT));
        return {
          ok: false,
          error: 'Impersonation session expired.',
          status: 401,
        };
      }
      console.warn(
        '[API] Auth expired and refresh failed, redirecting to login',
      );
      redirectToLogin();
      return {
        ok: false,
        error: 'Session expired. Please log in again.',
        status: 401,
      };
    }
  }

  // Parse response
  let data: T | undefined;
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      // Response body might be empty
    }
  }

  const isQuotaGatedPath = QUOTA_GATED_PATH_FRAGMENTS.some((fragment) =>
    endpoint.includes(fragment),
  );

  if (response.ok) {
    // Successful charge against a quota-gated endpoint — refresh billing
    // so the credit pill and Settings reflect the new balance.
    if (isQuotaGatedPath) {
      dispatchBillingInvalidate();
    }
    return {
      ok: true,
      data,
      status: response.status,
    };
  }

  // Extract error message, code, and params from structured response
  let errorMessage = 'Request failed';
  let errorCode: string | undefined;
  let errorParams: Record<string, unknown> = {};

  if (data && typeof data === 'object' && 'error' in data) {
    const errorObj = (data as { error: unknown }).error;

    if (errorObj && typeof errorObj === 'object') {
      // New format: { error: { code, message, params } }
      const structured = errorObj as {
        code?: string;
        message?: string;
        params?: Record<string, unknown>;
      };
      errorMessage = structured.message || 'Request failed';
      errorCode = structured.code;
      errorParams = structured.params || {};
    }
  }

  // Centralize the rich quota-error toast at the api boundary so every
  // quota-gated caller (throwing or result-shape) gets identical UX
  // without per-page wiring.
  const isQuotaBlocked = isQuotaBlockedRequest(endpoint, errorCode);
  let quotaToastShown = false;
  if (isQuotaBlocked) {
    const formatted = formatQuotaError({ errorCode, errorParams });
    if (formatted) {
      showQuotaErrorToast(formatted);
      quotaToastShown = true;
    }
    // A 4xx on a quota-gated path tells us the backend's view of usage
    // changed (concurrent-limit checks read active_jobs from the locked
    // subscription) — refresh billing to reflect it.
    dispatchBillingInvalidate();
  }

  return {
    ok: false,
    error: errorMessage,
    errorCode,
    errorParams,
    status: response.status,
    suppressSentry: isQuotaBlocked || undefined,
    quotaToastShown: quotaToastShown || undefined,
  };
}

/**
 * Fire `billing:invalidate` so the React Query listener refetches
 * `/api/billing/status`. Safe to call from any context — the listener
 * is registered once near the QueryClientProvider.
 */
function dispatchBillingInvalidate(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BILLING_INVALIDATE_EVENT));
}

/**
 * Get login URL for OAuth redirect
 */
export function getLoginUrl(): string {
  const redirectUrl = encodeURIComponent(window.location.origin);
  return `${API_HOST}/login?redirect_to=${redirectUrl}`;
}

/**
 * Redirect to login page
 */
export function redirectToLogin() {
  window.location.href = getLoginUrl();
}

/**
 * Get marketing/landing page URL
 */
export function getMarketingUrl(): string {
  // Match Godot's EnvConfig logic
  if (API_HOST.includes('api.posedraw.com')) {
    return 'https://www.posedraw.com';
  }
  return 'https://www.posedraw.com'; // Default to production marketing site
}

export { API_HOST };

/**
 * Get full API URL for an endpoint
 */
export function getApiUrl(endpoint: string): string {
  return `${API_HOST}/${endpoint.replace(/^\//, '')}`;
}

/**
 * Get authorization header value
 */
export function getAuthHeader(): string {
  const { accessToken } = getAuthTokens();
  return accessToken ? `Bearer ${accessToken}` : '';
}

// Multi-tab sync: when another tab refreshes tokens or logs out, update this tab
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event: StorageEvent) => {
    if (event.key !== TOKEN_KEYS.accessToken) return;

    if (event.newValue) {
      // Another tab refreshed tokens — push to Godot in this tab
      const refreshToken =
        localStorage.getItem(TOKEN_KEYS.refreshToken) || '';
      notifyGodotTokensUpdated(event.newValue, refreshToken);
      scheduleProactiveRefresh();
    } else {
      // Another tab cleared tokens. Cancel any pending proactive refresh in
      // this tab so a stale timer doesn't fire against a cleared session.
      cancelProactiveRefresh();
      const clearedBy = event.oldValue
        ? !!decodeJwtPayload(event.oldValue)?.impersonated_by
        : false;
      if (clearedBy) {
        window.dispatchEvent(new CustomEvent(IMPERSONATION_EXPIRED_EVENT));
      } else {
        redirectToLogin();
      }
    }
  });

  // Establish proactive refresh timer on module load (handles page reload)
  scheduleProactiveRefresh();
}
